import React, { useContext } from 'react';
import PropTypes from 'prop-types';

export const StateServiceContext = React.createContext(null);

class StateService {
  $state = null;

  constructor($state) {
    this.$state = $state;
  }

  gotoRelation(relationId, { gotoField = null } = {}) {
    if (!this.$state) {
      console.warn('$state not initilized');
      return;
    }
    this.$state.go('relations.detail', { relId: relationId, gotoField }, { reload: true });
  }

  gotoRelationHref(relationId) {
    if (!this.$state) {
      console.warn('$state not initilized');
      return;
    }
    return this.$state.href('relations.detail', { relId: relationId });
  }

  goToEmailTemplates() {
    if (!this.$state) {
      console.warn('$state not initilized');
      return;
    }
    this.$state.go('settings.interactiontemplates', { reload: true });
  }

  gotoWeekPlanner({ userId = null, executionDateFrom = null } = {}) {
    this.$state.go('taskflow.weekplanner', { userId, executionDateFrom }, { reload: true });
  }

  gotoTaskboard({
    relation = null,
    invoiceStreamId = null,
    taskTemplateId = null,
    plannedTaskId = null,
    openPlannedTaskDetailModal = false,
    kpi = null,
    userId = null,
    onlySubtasksForAssignedUser = null,
    userIsNextAssignedUser = null,
    deadlineFrom = null,
    deadlineUntil = null,
  } = {}) {
    if (!this.$state) {
      console.warn('$state not initilized');
      return;
    }
    this.$state.go(
      'taskflow.taskboard',
      {
        relation,
        invoiceStreamId: invoiceStreamId || (relation && relation.invoiceStreamId),
        taskTemplateId,
        plannedTaskId,
        openPlannedTaskDetailModal,
        kpi,
        userId,
        onlySubtasksForAssignedUser,
        userIsNextAssignedUser,
        deadlineFrom,
        deadlineUntil,
      },
      { reload: true },
    );
  }

  gotoTaskOverview(filters = null) {
    if (!this.$state) {
      console.warn('$state not initilized');
      return;
    }
    this.$state.go(
      'taskflow.overview',
      {
        filters,
      },
      { reload: true },
    );
  }
  gotoTaskList(filters = null) {
    if (!this.$state) {
      console.warn('$state not initilized');
      return;
    }
    this.$state.go(
      'taskflow.tasklist',
      {
        filters,
      },
      { reload: true },
    );
  }

  goToNewRelation() {
    if (!this.$state) {
      console.warn('$state not initilized');
      return;
    }
    this.$state.go('relations.new', { reload: true });
  }

  gotoJob(id) {
    if (!this.$state) {
      console.warn('$state not initilized');
      return;
    }

    this.$state.go('jobs.detail', { jobId: id });
  }

  goToJobOverview() {
    if (!this.$state) {
      console.warn('$state not initilized');
      return;
    }

    this.$state.go('jobs.overview');
  }

  gotoDocuments({ tags = null, relationId = null } = {}) {
    if (!this.$state) {
      console.warn('$state not initilized');
      return;
    }

    this.$state.go('documents', { tags, relationId });
  }

  gotoRegistrationOverview({
    period = null,
    relation = null,
    filters = null,
    finalInvoiceStreamId = null,
    userId = null, // use empty guid to force combo to all
    dateFrom = null, // Set period to 'all' when using custom date filters
    dateUntil = null,
    resourceId = null,
  } = {}) {
    if (!this.$state) {
      console.warn('$state not initilized');
      return;
    }

    this.$state.go('registrations.overview', {
      period,
      relation,
      filters,
      finalInvoiceStreamId,
      userId,
      dateFrom,
      dateUntil,
      resourceId,
    });
  }

  gotoDraftInvoices({ relation = null, invoiceId = null } = {}) {
    if (!this.$state) {
      console.warn('$state not initilized');
      return;
    }

    this.$state.go('invoicing.draft', { relation, invoiceId });
  }

  gotoInvoiceOverview({
    relationId = null,
    relation = null,
    invoiceId = null,
    filterType = null,
  } = {}) {
    if (!this.$state) {
      console.warn('$state not initilized');
      return;
    }

    this.$state.go('invoicing.overview', { relationId, relation, invoiceId, filterType });
  }

  // Generic goto state without helpers for parameters
  gotoState(stateName, params = {}) {
    if (!this.$state) {
      console.warn('$state not initilized');
      return;
    }

    this.$state.go(stateName, params);
  }

  getStateUrl(stateName, params = {}) {
    if (!this.$state) {
      console.warn('$state not initilized');
      return;
    }

    return this.$state.href(stateName, params);
  }

  goToInteractions(relationId, openMailModal) {
    if (!this.$state) {
      console.warn('$state not initilized');
      return;
    }
    if (!relationId) {
      console.warn('relationId not passed through');
      return;
    }

    this.$state.go('interactions', { relationId, openMailModal });
  }

  goToInteraction(relationId, interactionId) {
    if (!this.$state) {
      console.warn('$state not initilized');
      return;
    }
    if (!relationId) {
      console.warn('relationId not passed through');
      return;
    }

    this.$state.go('interactions', { relationId, interactionId });
  }

  gotoRelationOverview({
    tags = null,
    relationGroups = null,
    crmTypes = null,
    peppolStatus = null,
    ownCompanies = null,
    sendInvoicePeppolReady = null,
    type = null,
    countryCode = null,
    vatPeriodicities = null,
    vatNumberTypes = null,
    isPeppolRequired = null,
  }) {
    if (!this.$state) {
      console.warn('$state not initilized');
      return;
    }

    this.$state.go('relations.overview', {
      tags,
      relationGroups,
      crmTypes,
      peppolStatus,
      ownCompanies,
      sendInvoicePeppolReady,
      type,
      countryCode,
      vatPeriodicities,
      vatNumberTypes,
      isPeppolRequired,
    });
  }
}

const withAngularState = WrappedComponent => ({ $state, ...passThroughProps }) => {
  const service = new StateService($state);
  return (
    <StateServiceContext.Provider value={service}>
      <WrappedComponent {...passThroughProps} />
    </StateServiceContext.Provider>
  );
};

const injectStateService = WrappedComponent => props => (
  <StateServiceContext.Consumer>
    {service => <WrappedComponent stateService={service} {...props} />}
  </StateServiceContext.Consumer>
);
export const useStateService = () => {
  const ctx = useContext(StateServiceContext);
  if (!ctx) {
    throw new Error('Wrap app with StateServiceContext');
  }
  return ctx;
};

const stateServicePropType = PropTypes.instanceOf(StateService);

export { withAngularState, injectStateService, StateService, stateServicePropType };

export default { withAngularState, injectStateService, StateService, stateServicePropType };
